import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, FormControlLabel, IconButton, Stack, TextField } from "@mui/material";
import CustomSwitch from "@components/CustomSwitch";
import CustomLabelInput from "@components/CustomLabelInput";
import { useTranslation } from "react-i18next";
import { Avatar } from "@models/avatar";
import { Voice } from "@models/voice";
import styled from "@emotion/styled";
import { Controller, UseFormReturn } from "react-hook-form";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { AzureTextToSpeechService } from "@service/text-to-speech/azure-text-to-speech-service";
import { useGetTokenQuery } from "@store/avatarApi";
import { TextToSpeechService } from "@service/text-to-speech/text-to-speech-service";
import { AvatarTokenConfig, AvatarType } from "@models/avatar-config";
import VoicePhrases from "../../../public/locales/voice.json";
import { Language } from "@models/language";
import Loader from "@components/Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { setVoiceId } from "@store/companionFormSlice";
import { UseControllerProps } from "react-hook-form/dist/types/controller";

const TOKEN_POLLING_INTERVAL = 9 * 60 * 1000; // 9 minutes

interface Phrase {
  translation: { phrase: string };
}

export interface CompanionFormType {
  name?: string;
  avatarId?: string;
  voiceId?: string;
  isDefault?: boolean;
  openWithDateLocation?: boolean;
}

const Wrapper = styled.div<{ active: boolean }>`
  padding: 1px;
  border-radius: 4px;
  border: solid 2px;
  ${(props) => ({
    borderColor: props.active ? "#1E64DDFF" : "#F3F4F6FF",
  })};
  box-sizing: border-box;
  overflow: hidden;
  cursor: pointer;
`;

const AvatarPicture = styled.img`
  object-fit: cover;
  max-width: 320px;
  max-height: 320px;
  height: 100%;
`;

const AvatarWidget: FC<{ avatar: Avatar; selected: boolean }> = ({
  avatar,
  selected,
}) => {
  return (
    <Wrapper active={selected}>
      <div style={{ height: "130px" }}>
        <AvatarPicture id={`avatar-image-${avatar.externalId}`} src={`${avatar.imageUrl}`} />
      </div>
    </Wrapper>
  );
};

const getTextToSpeechService = (type: AvatarType): TextToSpeechService => {
  switch (type) {
    case AvatarType.Azure:
      return new AzureTextToSpeechService();
    default:
      throw new Error("Not implemented");
  }
};

const VoiceWidget: FC<
  {
    voice: Voice;
    language: Language;
    tokenConfig?: AvatarTokenConfig;
  } & UseControllerProps
> = ({ voice, language, tokenConfig, ...props }) => {
  const { t } = useTranslation();
  const playingVoiceId = useSelector(
    (state: RootState) => state.companionForm.voiceId,
  );
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(
    voice.previewUrl ? new Audio(voice.previewUrl) : null,
  );
  const dispatch = useDispatch();

  const ttsService = useMemo(() => {
    if (!tokenConfig) {
      return;
    }
    const service = getTextToSpeechService(tokenConfig.type);
    if (service) {
      service.init(tokenConfig);
    }
    return service;
  }, [tokenConfig]);

  const toggleAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      setPlaying(true);
      const p = audio.play();
      if (p !== undefined) {
        void p.catch(() => {
          // rapid toggle / pause before play settles — safe to ignore
        });
      }
      audio.addEventListener(
        "ended",
        () => {
          setPlaying(false);
          dispatch(setVoiceId(null));
        },
        false,
      );
    }
  }, [playing, dispatch]);

  const cancelSpeechToText = useCallback(async () => {
    await ttsService?.cancelSpeech();
    ttsService?.destroy();
    setPlaying(false);
  }, [ttsService]);

  const convertSpeechToText = useCallback(
    async (text: string) => {
      try {
        setPlaying(true);
        await ttsService!.convertTextToSpeechAndPlay(
          text,
          voice.externalId,
          language,
        );
      } finally {
        dispatch(setVoiceId(null));
        setPlaying(false);
      }
    },
    [ttsService, language, voice.externalId, dispatch],
  );

  useEffect(() => {
    const phrase: Phrase = VoicePhrases[language];
    if (playingVoiceId !== voice.id && playing) {
      phrase && ttsService ? cancelSpeechToText() : toggleAudio();
      return;
    }
    if (playingVoiceId === voice.id && !playing) {
      phrase && ttsService
        ? convertSpeechToText(phrase.translation.phrase)
        : toggleAudio();
      return;
    }
  }, [
    ttsService,
    playing,
    playingVoiceId,
    cancelSpeechToText,
    convertSpeechToText,
    toggleAudio,
    language,
    voice.id,
  ]);

  const handleClick = (e: any) => {
    e.preventDefault();
    dispatch(setVoiceId(voice.id === playingVoiceId ? null : voice.id));
  };

  useEffect(() => {
    const audio = audioRef.current;

    return () => {
      if (audio) {
        audio.pause();
      }
      cancelSpeechToText();
    };
  }, [cancelSpeechToText]);

  const hasPreview = !!voice.previewUrl || voice.provider === AvatarType.Azure;

  return (
    <Controller
      render={({ field: { value, onChange } }) => (
        <Wrapper active={value === voice.id} onClick={() => onChange(voice.id)}>
          <Stack
            flexDirection="row"
            alignItems="center"
            height="40px"
            padding="4px"
            paddingLeft="10px"
            gap="4px"
          >
            {voice.name}
            {hasPreview && (
              <IconButton
                size="small"
                aria-label={t("header.settingButton")}
                id="basic-button"
                onClick={handleClick}
              >
                {playing ? (
                  <StopCircleOutlinedIcon />
                ) : (
                  <PlayCircleOutlineIcon />
                )}
              </IconButton>
            )}
          </Stack>
        </Wrapper>
      )}
      {...props}
    />
  );
};

const CompanionForm: FC<{
  form: UseFormReturn<CompanionFormType>;
  avatars?: Avatar[];
  voices?: Voice[];
  language: Language;
  showConversationSettings?: boolean;
}> = ({
  form: {
    control,
    watch,
    register,
    setValue,
    formState: { errors },
  },
  avatars,
  voices,
  language,
  showConversationSettings = false,
}) => {
  const { t } = useTranslation();
  const avatarId = watch("avatarId");
  const selectedAvatar = useMemo(
    () => avatars?.find((a) => a.id === avatarId),
    [avatars, avatarId],
  );

  const { data: tokenConfig, isLoading: isLoadingTokenConfig } =
    useGetTokenQuery(
      { provider: selectedAvatar?.provider! },
      {
        pollingInterval: TOKEN_POLLING_INTERVAL,
        skip: !selectedAvatar?.provider,
      },
    );

  const filterVoices = useMemo(() => {
    return voices?.filter(
      (v) =>
        selectedAvatar?.gender === v.gender &&
        selectedAvatar.provider === v.provider &&
        (!v.languages || (v.languages && v.languages.includes(language))),
    );
  }, [selectedAvatar, language, voices]);

  return (
    <Stack gap="16px">
      <CustomLabelInput
        label={t("companions.drawer.name.label")}
        id="companionName"
        required={true}
      >
        <TextField
          {...register(`name`, { required: true })}
          error={!!errors?.name}
          id="companionName"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          placeholder={t("companions.drawer.name.placeholder")}
        />
      </CustomLabelInput>

      <CustomLabelInput
        label={t("companions.drawer.avatar.label")}
        id="avatar"
        required={true}
      >
        <Box display="flex" gap="2px" flexWrap="wrap">
          {avatars?.map((avatar) => (
            <Box
              key={avatar.id}
              onClick={() => setValue("avatarId", avatar.id)}
            >
              <AvatarWidget avatar={avatar} selected={avatar.id === avatarId} />
            </Box>
          ))}
        </Box>
      </CustomLabelInput>

      <CustomLabelInput
        label={t("companions.drawer.voice.label")}
        id="avatar"
        error={!!errors.voiceId}
        required={true}
      >
        <Box display="flex" gap="2px" flexWrap="wrap" width="100%">
          {!isLoadingTokenConfig ? (
            filterVoices?.map((voice) => (
              <VoiceWidget
                key={voice.id}
                name="voiceId"
                rules={{ required: true }}
                control={control}
                voice={voice}
                language={language}
                tokenConfig={tokenConfig}
              />
            ))
          ) : (
            <Loader />
          )}
        </Box>
      </CustomLabelInput>

      {showConversationSettings && (
        <CustomLabelInput
          label={t("companions.drawer.openWithDateLocation.label")}
          id="openWithDateLocation"
        >
          <Controller
            name="openWithDateLocation"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <CustomSwitch
                    checked={value ?? true}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                }
                label={t("companions.drawer.openWithDateLocation.helper")}
              />
            )}
          />
        </CustomLabelInput>
      )}
    </Stack>
  );
};

export default CompanionForm;
